import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Header,
  Button,
  Loader,
  Divider,
  Menu,
  Dropdown,
  Dimmer,
} from 'semantic-ui-react'

import { fetchAdminTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { getUserById } from '@/selectors/user.selectors'
import {
  clearTransactionFilters,
  clearAndUpdateTransactionFilters,
} from '@/reducers/finances/transactionFiltersReducer'
import { UserBreadcrumbs } from './user-record/UserBreadcrumbs'
import { LockedBooksMessage } from '@/features/transactions/LockedBooksMessage'
import { adminFetchSingleUser } from '../../actions/admin/adminAllUsersActions'
import { useReselector } from '@/utils/sharedHooks'
import { UserStateLabel } from './user-record/UserAccountStates/UserStateLabel'
import { UserAccountCanceledAlert } from './user-record/UserAccountStates/UserAccountCanceledAlert'
import { PaginatedTransactionTable } from './paginated-transaction-table/PaginatedTransactionTable'
import { useAppDispatch } from '@/utils/typeHelpers'
import { UserFreeTrialAlert } from './user-record/UserAccountStates/UserFreeTrialAlert'

const UserTransactionPanel = () => {
  const dispatch = useAppDispatch()
  const { userId } = useParams()
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const user = useReselector(getUserById, userId)

  useEffect(() => {
    dispatch(fetchAdminTransactionCategoriesIfNeeded())
  }, [dispatch])

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      if (userId) {
        await Promise.all([
          dispatch(adminFetchSingleUser(userId)),
          dispatch(
            clearAndUpdateTransactionFilters({ userId: Number(userId) })
          ),
        ])
      }
      setLoading(false)
    }

    fetch()
  }, [dispatch, userId])

  // Clear transaction filters when navigating away
  useEffect(() => {
    return () => {
      dispatch(clearTransactionFilters())
    }
  }, [dispatch])

  const lockedYear =
    user?.booksLockedForAdminYear || user?.booksLockedForUserYear

  return (
    <div id="adminPanel">
      {user && (
        <>
          <UserBreadcrumbs userId={user.id} pageName={'Transactions'} />
          <UserFreeTrialAlert userId={user.id} />
          <Divider />
          {lockedYear && (
            <LockedBooksMessage
              year={lockedYear}
              admin
              userOnlyLocked={
                Boolean(user.booksLockedForUserYear) &&
                !user.booksLockedForAdminYear
              }
            />
          )}
          <div className="flexbox">
            <div>
              <Header as="h3">
                <Link to={`/admin/finances/records/${user.id}`}>
                  <Button circular icon="arrow left" />
                </Link>
                All Transactions for User: {user.firstName} {user.lastName}
              </Header>
            </div>

            <Menu.Menu position="right">
              <Dropdown button fluid text="Other Actions">
                <Dropdown.Menu direction="left">
                  <Dropdown.Item
                    text="View Imported"
                    onClick={() =>
                      navigate(
                        `/admin/finances/transactions/${user.id}/imported`
                      )
                    }
                  />
                </Dropdown.Menu>
              </Dropdown>
            </Menu.Menu>
          </div>
          <br />
          <div>
            <UserStateLabel user={user} />
            <br />
            <br />
            <UserAccountCanceledAlert user={user} />
          </div>
        </>
      )}
      {loading && (
        <Dimmer active inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>
      )}
      {!loading && user && <PaginatedTransactionTable user={user} />}
    </div>
  )
}

export default UserTransactionPanel
